import { buildFetchConfig } from '@/utils/fetchUtils';
import type {
  ListSkillsResponse,
  UserSkill,
  TenantSkill,
  SkillCreateRequest,
  SkillUpdateRequest,
  ToggleSystemSkillResponse,
} from '@Types/skills';

class HttpError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message || `Request failed: ${status}`);
    this.status = status;
    this.body = body;
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const { baseURL, headers } = buildFetchConfig();
  const response = await fetch(`${baseURL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers || {}) },
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // non-JSON error body — leave null
    }
    throw new HttpError(response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
};

export const listSkills = (): Promise<ListSkillsResponse> =>
  request<ListSkillsResponse>('/skills');

export const getSkill = (skillId: string): Promise<UserSkill | TenantSkill> =>
  request<UserSkill | TenantSkill>(`/skills/${skillId}`);

export const createSkill = (
  body: SkillCreateRequest,
): Promise<UserSkill | TenantSkill> =>
  request<UserSkill | TenantSkill>('/skills', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateSkill = (
  skillId: string,
  body: SkillUpdateRequest,
): Promise<UserSkill | TenantSkill> =>
  request<UserSkill | TenantSkill>(`/skills/${skillId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const deleteSkill = (skillId: string): Promise<void> =>
  request<void>(`/skills/${skillId}`, { method: 'DELETE' });

export const toggleSystemSkill = (
  skillId: string,
  enabled: boolean,
): Promise<ToggleSystemSkillResponse> =>
  request<ToggleSystemSkillResponse>(
    `/skills/system/${encodeURIComponent(skillId)}/toggle`,
    {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    },
  );

export { HttpError };
